import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Save, Sparkles } from "lucide-react";
import { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { ClientFacingNotesField } from "@/components/invoice/ClientFacingNotesField";
import { PaymentQrOptionsField } from "@/components/invoice/PaymentQrOptionsField";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import {
  FieldError,
  FieldLabel,
  Input,
  Select,
  Textarea,
} from "@/components/ui/Field";
import {
  centsToDecimal,
  currencyOptionLabel,
} from "@/domain/billing";
import {
  CURRENCIES,
  type ClientProfile,
  type PaymentQrAssetMap,
} from "@/domain/types";
import {
  invoiceFormSchema,
  newFormItem,
  type InvoiceFormValues,
} from "@/features/invoices/invoice-form";
import { defaultPaymentQrSettings } from "@/features/invoices/payment-qr";
import { DueRuleFields } from "./DueRuleFields";
import { InvoiceSummary } from "./InvoiceSummary";
import { LineItemEditor } from "./LineItemEditor";
import { PartyFields } from "./PartyFields";

interface Props {
  initialValues: InvoiceFormValues;
  clients: ClientProfile[];
  paymentQrAssets: PaymentQrAssetMap;
  submitLabel: string;
  onSubmit: (
    values: InvoiceFormValues,
  ) => void | Promise<void>;
  cancelTo?: string;
}

function SectionTitle({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="mb-5">
      <h2 className="text-lg font-black text-ink">
        {title}
      </h2>
      <p className="mt-1 text-sm leading-6 text-ink-muted">
        {description}
      </p>
    </div>
  );
}

export function InvoiceForm({
  initialValues,
  clients,
  paymentQrAssets,
  submitLabel,
  onSubmit,
  cancelTo = "/app",
}: Props) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: initialValues,
    mode: "onBlur",
  });

  const selectedClientId = watch("clientId");
  const currency = watch("currency");

  const selectedClient = useMemo(
    () =>
      clients.find(
        (client) => client.id === selectedClientId,
      ),
    [clients, selectedClientId],
  );

  const defaultBillingType =
    selectedClient?.defaults.billingType ??
    getValues("items.0.billingType") ??
    "hourly";

  const defaultRate = centsToDecimal(
    selectedClient?.defaults.rateCents ??
      selectedClient?.defaults.hourlyRateCents ??
      0,
  );

  const defaultRateUnit =
    selectedClient?.defaults.rateUnit ??
    (defaultBillingType === "hourly"
      ? "hour"
      : defaultBillingType === "fixed"
        ? "project"
        : "item");

  const applyClient = (id: string) => {
    setValue("clientId", id);

    const client = clients.find(
      (item) => item.id === id,
    );

    if (!client) {
      return;
    }

    setValue(
      "client",
      {
        name: client.name,
        company: client.company,
        email: client.email,
        phone: client.phone,
        address: structuredClone(client.address),
      },
      { shouldDirty: true },
    );

    setValue("currency", client.defaults.currency, {
      shouldDirty: true,
    });

    setValue(
      "paymentQr",
      defaultPaymentQrSettings(client.defaults.currency, paymentQrAssets),
      { shouldDirty: true },
    );

    setValue(
      "billingCycle",
      client.defaults.billingCycle,
      { shouldDirty: true },
    );

    setValue(
      "paymentTerms",
      client.defaults.paymentTerms,
      { shouldDirty: true },
    );

    setValue(
      "paymentInstructions",
      client.defaults.paymentInstructions,
      { shouldDirty: true },
    );

    const rule = client.defaults.dueRule;

    setValue("dueKind", rule.kind, {
      shouldDirty: true,
    });

    if (rule.kind === "specific") {
      setValue("dueSpecificDate", rule.date);
    }

    if (rule.kind === "net") {
      setValue("dueNetDays", rule.days);
    }

    if (rule.kind === "weekly") {
      setValue("dueWeekday", rule.weekday);
    }

    if (rule.kind === "every_n_weeks") {
      setValue(
        "dueIntervalWeeks",
        rule.intervalWeeks,
      );
      setValue("dueAnchorDate", rule.anchorDate);
    }

    if (rule.kind === "monthly") {
      setValue("dueMonthlyDay", rule.dayOfMonth);
    }

    if (rule.kind === "custom") {
      setValue("dueCustomLabel", rule.label);
    }

    const clientRate = centsToDecimal(
      client.defaults.rateCents ??
        client.defaults.hourlyRateCents ??
        0,
    );

    const clientUnit =
      client.defaults.rateUnit ||
      (client.defaults.billingType === "hourly"
        ? "hour"
        : client.defaults.billingType === "fixed"
          ? "project"
          : "item");

    const existingItems = getValues("items");
    const firstItem = existingItems[0];

    const shouldReplaceStarterItem =
      existingItems.length === 1 &&
      firstItem &&
      firstItem.description.trim() === "";

    if (shouldReplaceStarterItem) {
      setValue(
        "items",
        [
          newFormItem(
            client.defaults.billingType,
            clientRate,
            clientUnit,
          ),
        ],
        { shouldDirty: true },
      );
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_310px]">
        <div className="space-y-6">
          <Card className="p-5 sm:p-6">
            <SectionTitle
              title="Client profile"
              description="Choose a saved client to prefill their currency, calculation method, rate unit, billing cycle, and payment agreement. Every invoice keeps its own snapshot."
            />

            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
              <div>
                <FieldLabel htmlFor="client-profile">
                  Saved client
                </FieldLabel>
                <Select
                  id="client-profile"
                  value={selectedClientId}
                  onChange={(event) =>
                    applyClient(event.target.value)
                  }
                >
                  <option value="">
                    Manual client details
                  </option>
                  {clients.map((client) => (
                    <option
                      key={client.id}
                      value={client.id}
                    >
                      {client.name}
                      {client.company
                        ? ` — ${client.company}`
                        : ""}
                    </option>
                  ))}
                </Select>
              </div>

              <Link
                to="/app/clients/new"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 text-sm font-semibold text-ink hover:bg-surface-muted"
              >
                <Sparkles className="h-4 w-4" />
                New client
              </Link>
            </div>
          </Card>

          <Card className="p-5 sm:p-6">
            <SectionTitle
              title="Your company"
              description="The issuer information saved with this invoice."
            />
            <PartyFields
              prefix="company"
              companyMode
              register={register}
              errors={errors}
            />
          </Card>

          <Card className="p-5 sm:p-6">
            <SectionTitle
              title="Client details"
              description="Editable for this invoice even when a saved client profile was used."
            />
            <PartyFields
              prefix="client"
              register={register}
              errors={errors}
            />
          </Card>

          <Card className="p-5 sm:p-6">
            <SectionTitle
              title="Invoice and service coverage"
              description="Invoice date, work coverage, billing frequency, currency, and optional tax are stored per invoice."
            />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <FieldLabel
                  required
                  htmlFor="issue-date"
                >
                  Invoice date
                </FieldLabel>
                <Input
                  id="issue-date"
                  type="date"
                  {...register("issueDate")}
                />
                <FieldError
                  message={errors.issueDate?.message}
                />
              </div>

              <div>
                <FieldLabel htmlFor="service-start">
                  Service from
                </FieldLabel>
                <Input
                  id="service-start"
                  type="date"
                  {...register("serviceStartDate")}
                />
                <FieldError
                  message={
                    errors.serviceStartDate?.message
                  }
                />
              </div>

              <div>
                <FieldLabel htmlFor="service-end">
                  Service to
                </FieldLabel>
                <Input
                  id="service-end"
                  type="date"
                  {...register("serviceEndDate")}
                />
                <FieldError
                  message={
                    errors.serviceEndDate?.message
                  }
                />
              </div>

              <div>
                <FieldLabel htmlFor="billing-cycle">
                  Billing cycle
                </FieldLabel>
                <Select
                  id="billing-cycle"
                  {...register("billingCycle")}
                >
                  <option value="one_time">
                    One-time / project
                  </option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">
                    Bi-weekly
                  </option>
                  <option value="monthly">
                    Monthly / retainer
                  </option>
                  <option value="custom">Custom</option>
                </Select>
              </div>

              <div>
                <FieldLabel htmlFor="currency">
                  Currency
                </FieldLabel>
                <Select
                  id="currency"
                  {...register("currency")}
                >
                  {CURRENCIES.map((currencyCode) => (
                    <option
                      key={currencyCode}
                      value={currencyCode}
                    >
                      {currencyOptionLabel(currencyCode)}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <FieldLabel htmlFor="tax-rate">
                  Tax rate (%)
                </FieldLabel>
                <Input
                  id="tax-rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  {...register("taxRatePercent", {
                    valueAsNumber: true,
                  })}
                />
                <FieldError
                  message={
                    errors.taxRatePercent?.message
                  }
                />
              </div>
            </div>
          </Card>

          <Card className="p-5 sm:p-6">
            <SectionTitle
              title="Agreed payment schedule"
              description="Choose no due date, a fixed date, Net terms, weekly schedules, bi-weekly anchors, monthly dates, or custom terms."
            />
            <DueRuleFields
              register={register}
              watch={watch}
              errors={errors}
            />
          </Card>

          <Card className="p-5 sm:p-6">
            <LineItemEditor
              control={control}
              register={register}
              setValue={setValue}
              errors={errors}
              currency={currency}
              defaultBillingType={defaultBillingType}
              defaultRate={defaultRate}
              defaultRateUnit={defaultRateUnit}
            />
          </Card>

          <Card className="p-5 sm:p-6">
            <SectionTitle
              title="Payment and notes"
              description="Payment instructions and public notes appear to the client. Internal notes remain visible only in your private dashboard."
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel htmlFor="payment-terms">
                  Payment terms
                </FieldLabel>
                <Textarea
                  id="payment-terms"
                  placeholder="e.g. Payable through the agreed channel"
                  {...register("paymentTerms")}
                />
              </div>

              <div>
                <FieldLabel htmlFor="payment-instructions">
                  Payment instructions
                </FieldLabel>
                <Textarea
                  id="payment-instructions"
                  placeholder="e.g. Scan the preferred QR and use the invoice number as the payment reference."
                  {...register("paymentInstructions")}
                />
              </div>

              <Controller
                control={control}
                name="paymentQr"
                render={({ field }) => (
                  <PaymentQrOptionsField
                    value={field.value}
                    currency={currency}
                    assets={paymentQrAssets}
                    disabled={isSubmitting}
                    onChange={field.onChange}
                  />
                )}
              />

              <Controller
                control={control}
                name="publicNotes"
                render={({ field }) => (
                  <ClientFacingNotesField
                    id="public-notes"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />

              <div>
                <FieldLabel htmlFor="internal-notes">
                  Internal notes
                </FieldLabel>
                <Textarea
                  id="internal-notes"
                  placeholder="Private reminders that never appear on the public link or PDFs"
                  {...register("internalNotes")}
                />
              </div>
            </div>
          </Card>
        </div>

        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <InvoiceSummary control={control} />

          <Card className="p-4">
            <Button
              type="submit"
              variant="brand"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
            >
              <Save className="h-5 w-5" />
              {isSubmitting ? "Saving…" : submitLabel}
            </Button>

            <Link
              to={cancelTo}
              className="mt-2 inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-ink-muted hover:bg-surface-muted hover:text-ink"
            >
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </Link>
          </Card>
        </aside>
      </div>
    </form>
  );
}
